import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bell, Radio, X } from "lucide-react";
import api from "@/lib/api";

const OPT_IN_KEY = "clbc-live-alerts-opt-in";
const DISMISS_KEY = "clbc-live-alerts-dismissed";

/**
 * Polls the CLBC YouTube live-status every 45s. When user opts in, sends
 * a browser Notification the moment the channel goes live (from offline).
 * Also shows a subtle bottom-right prompt to opt in on first visit.
 */
export default function LiveNotifier() {
  const [optedIn, setOptedIn] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem(OPT_IN_KEY) === "1"
  );
  const [showPrompt, setShowPrompt] = useState(false);
  const wasLiveRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("Notification" in window)) return;
    const dismissed = localStorage.getItem(DISMISS_KEY) === "1";
    if (!optedIn && !dismissed) {
      const t = setTimeout(() => setShowPrompt(true), 8000);
      return () => clearTimeout(t);
    }
  }, [optedIn]);

  useEffect(() => {
    if (!optedIn) return;
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get("/youtube/live-status");
        const isLive = !!data?.is_live;
        if (isLive && !wasLiveRef.current) {
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification("🔴 CLBC is LIVE now", {
              body: data.title || "Christ Love Breed Church is streaming — join the service.",
              icon: "https://customer-assets.emergentagent.com/job_ece042c9-c81a-4391-a5bf-8fcc2b022067/artifacts/tox2yx3r_IMG_0257.jpeg",
              tag: "clbc-live",
            });
          }
          toast.success("CLBC is LIVE now — click to watch!", {
            duration: 10000,
            action: { label: "Watch", onClick: () => (window.location.href = "/watch-live") },
          });
        }
        wasLiveRef.current = isLive;
      } catch {
        /* ignore */
      }
    };
    check();
    const timer = setInterval(() => !cancelled && check(), 45000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [optedIn]);

  const enable = async () => {
    if (!("Notification" in window)) {
      toast.error("Your browser doesn't support notifications.");
      return;
    }
    let perm = Notification.permission;
    if (perm === "default") perm = await Notification.requestPermission();
    if (perm !== "granted") {
      toast.error("Please allow notifications in your browser to get live alerts.");
      return;
    }
    localStorage.setItem(OPT_IN_KEY, "1");
    setOptedIn(true);
    setShowPrompt(false);
    toast.success("You'll be notified the moment CLBC goes live 🎉");
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShowPrompt(false);
  };

  if (!showPrompt || optedIn) return null;

  return (
    <div
      role="dialog"
      aria-label="Live service alerts"
      data-testid="live-notifier-prompt"
      className="fixed bottom-6 right-6 z-40 w-[320px] rounded-2xl shadow-2xl overflow-hidden animate-fade-up"
    >
      <div className="bg-royal-800 text-white p-5 relative">
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-gold/20 text-gold flex items-center justify-center">
            <Radio className="w-5 h-5" />
          </div>
          <div>
            <div className="font-serif text-lg leading-tight">Get a live alert</div>
            <p className="text-xs text-white/70 mt-1 leading-relaxed">
              Get a browser notification the moment CLBC starts streaming on YouTube — never miss a service.
            </p>
          </div>
        </div>
        <button
          onClick={enable}
          data-testid="live-notifier-enable"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-royal-800 hover:bg-gold-500 font-semibold text-sm py-2.5 transition-colors"
        >
          <Bell className="w-4 h-4" /> Enable live alerts
        </button>
      </div>
    </div>
  );
}

/** Reusable button (e.g. in WatchLive fallback panel) */
export function EnableLiveAlertsButton({ className = "btn-primary text-sm" }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (!("Notification" in window)) return toast.error("Notifications not supported");
    setBusy(true);
    try {
      let perm = Notification.permission;
      if (perm === "default") perm = await Notification.requestPermission();
      if (perm !== "granted") return toast.error("Notifications blocked. Enable them in your browser settings.");
      localStorage.setItem(OPT_IN_KEY, "1");
      toast.success("You'll be notified when CLBC goes live 🎉");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={onClick} disabled={busy} className={className} data-testid="enable-live-alerts">
      <Bell className="w-4 h-4" /> Get Live Notifications
    </button>
  );
}
